import { useCallback, useEffect, useState } from 'react';
import { Clock, X } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import Button from '../ui/Button';
import timeInLieuService, { type MyTilStatus } from '../../services/timeInLieu';
import { formatDateTimeInTz, formatLocalDateInTz } from '../../utils/timezone';

/**
 * Overtime or time in lieu, for the signed-in employee (My Timesheet).
 *
 * Overtime is paid. An employee who prefers time off asks here, reads and
 * accepts the agreement, and HR approves it for the employer; only then is
 * overtime banked, hour for hour. Either side can end it.
 */
export default function TilChoiceCard({ orgTz, onChanged, openAgreement, why }: {
  orgTz: string;
  /** The choice moved: the page's time in lieu balance may read differently. */
  onChanged?: () => void;
  /** Open the agreement straight away (a link here asked for it). */
  openAgreement?: boolean;
  /** Why they were sent here, shown above the agreement. */
  why?: string;
}) {
  const { t } = useTranslation('timesheet', { keyPrefix: 'TilChoiceCard' });
  const [status, setStatus] = useState<MyTilStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [ending, setEnding] = useState(false);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await timeInLieuService.getMine();
      if (res.success && res.data) setStatus(res.data);
    } catch {
      // A card that cannot load says nothing rather than a wrong answer.
      setStatus(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Opened from elsewhere (working through lunch): show the agreement, unless
  // there is already a request or an agreement to show instead.
  const [autoOpened, setAutoOpened] = useState(false);
  useEffect(() => {
    if (!openAgreement || autoOpened || !status) return;
    setAutoOpened(true);
    if (!status.open) setReading(true);
  }, [openAgreement, autoOpened, status]);

  const act = async (fn: () => Promise<{ success: boolean; error?: { message?: string } }>, done: string) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fn();
      if (!res.success) {
        setError(res.error?.message || t('failed'));
        return;
      }
      setReading(false);
      setAgreed(false);
      setEnding(false);
      setReason('');
      setNotice(done);
      await load();
      onChanged?.();
    } catch (err) {
      const e = err as { response?: { data?: { error?: { message?: string; code?: string } } }; message?: string; code?: string };
      setError(e?.response?.data?.error?.message || e?.message || t('failed'));
      // The text moved under the reader: show the new one.
      if (e?.response?.data?.error?.code === 'AGREEMENT_CHANGED' || e?.code === 'AGREEMENT_CHANGED') {
        setAgreed(false);
        load();
      }
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;

  const open = status.open;
  const lastDeclined = !open ? status.history.find(h => h.status === 'declined') : null;
  const lastEnded = !open ? status.history.find(h => h.status === 'ended') : null;
  const recentDecline = lastDeclined && (!lastEnded || lastDeclined.id > lastEnded.id) ? lastDeclined : null;

  return (
    <div className="mb-6 p-4 bg-white border border-neutral-200 rounded-lg">
      <div className="flex items-start gap-3">
        <Clock className="h-5 w-5 text-primary-600 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1 text-sm text-neutral-700 space-y-2">
          <p className="font-semibold text-neutral-900">{t('title')}</p>

          {!open && (
            <>
              <p>
                <Trans t={t} i18nKey="paidIntro" components={{ strong: <strong /> }} />
              </p>
              {recentDecline && (
                <p className="text-amber-800">
                  {recentDecline.decision_note
                    ? t('declinedWithNote', { note: recentDecline.decision_note })
                    : t('declined')}
                </p>
              )}
              <Button size="sm" variant="outline" onClick={() => { setNotice(null); setReading(true); }}>
                {t('ask')}
              </Button>
            </>
          )}

          {open?.status === 'pending' && (
            <>
              <p>
                {t('pending', { when: formatDateTimeInTz(open.requested_at, orgTz) })}
              </p>
              <Button size="sm" variant="ghost" disabled={busy}
                onClick={() => act(() => timeInLieuService.cancelRequest(), t('withdrawn'))}>
                {t('withdraw')}
              </Button>
            </>
          )}

          {open?.status === 'approved' && (
            <>
              <p>
                {open.effective_from
                  ? <Trans t={t} i18nKey="bankedSince" values={{ date: formatLocalDateInTz(open.effective_from, orgTz) }} components={{ strong: <strong /> }} />
                  : <Trans t={t} i18nKey="banked" components={{ strong: <strong /> }} />}
              </p>
              {ending ? (
                <div className="space-y-2">
                  <label htmlFor="til-end-own" className="block font-medium text-neutral-700">{t('whyOptional')}</label>
                  <textarea
                    id="til-end-own"
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    rows={2}
                    maxLength={500}
                    className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                  />
                  <p className="text-xs text-neutral-500">
                    {t('endHelp')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" disabled={busy}
                      onClick={() => act(() => timeInLieuService.endMine(reason), t('ended'))}>
                      {t('goBack')}
                    </Button>
                    <Button size="sm" variant="ghost" disabled={busy} onClick={() => setEnding(false)}>{t('keep')}</Button>
                  </div>
                </div>
              ) : (
                <Button size="sm" variant="ghost" onClick={() => { setNotice(null); setEnding(true); }}>
                  {t('goBack')}
                </Button>
              )}
            </>
          )}

          {notice && <p className="text-green-700">{notice}</p>}
          {error && !reading && <p className="text-red-600">{error}</p>}
        </div>
      </div>

      {reading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setReading(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
              <h3 className="text-lg font-semibold text-neutral-900">{t('agreementTitle')}</h3>
              <button onClick={() => setReading(false)} className="p-1 rounded hover:bg-neutral-100 text-neutral-400 hover:text-neutral-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto">
              {why && (
                <p className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900">{why}</p>
              )}
              <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-700 whitespace-pre-wrap break-words">
                {status.agreement.text}
              </div>
              <label className="flex items-start gap-2 text-sm text-neutral-800">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={e => setAgreed(e.target.checked)}
                  className="mt-0.5 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                />
                <span>{t('agreeCheckbox')}</span>
              </label>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <div className="flex flex-wrap justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setReading(false)}
                  className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50"
                >
                  {t('cancel')}
                </button>
                <Button disabled={!agreed || busy}
                  onClick={() => act(() => timeInLieuService.request(status.agreement), t('sent'))}>
                  {busy ? t('sending') : t('acceptAndSend')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
